import React, { ChangeEvent, FormEvent, ReactNode, SyntheticEvent, useEffect, useReducer } from 'react'
import { FormContext } from 'contexts/FormContext'
import { useDispatch } from 'react-redux'
import { RENDER_FORM_STATE, BEHAVIOUR_FORM_STATE, VIEW_FORM_STATE } from 'config/constants/editorSettings'
import { EDITOR_MAIN_SETTINGS } from 'config/constants/mainView'

type InitialState<T> = {
  [K in keyof T]: T[K]
}

type FormInputData<T> = {
  [K in keyof T]?: T[K]
}

type Props<T> = {
  children: ReactNode | ReactNode[]
  initialState: InitialState<T>
  action: (formInput: InitialState<T>) => ({
    type: string,
    payload: unknown
  })
  onClose():void
}

const SET_DATA_ACTION = 'set_data'
const RESET_ACTION = 'reset'

type FormActionType = typeof SET_DATA_ACTION | typeof RESET_ACTION

export const FormContextProvider = <T, >({ children, initialState, action, onClose }: Props<T>) => {
  const dispatch = useDispatch()
  const [ formState, formDispatch ] = useReducer(
    (state: InitialState<T>, action: { type: FormActionType, payload?: FormInputData<T> }) => {
      switch (action.type) {
        case SET_DATA_ACTION:
          return { ...state, ...action.payload }
        case RESET_ACTION:
          return { ...initialState }
        default:
          return state
      }
    }, { ...initialState }
  )

  const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const input = e.target as HTMLInputElement
    const value = input.type === 'checkbox'
      ? (input.value || Number(input.checked) || 0)
      : input.value
    formDispatch({
      type: SET_DATA_ACTION,
      payload: { [input.name]: value } as unknown as FormInputData<T>,
    })
    dispatch(action({ ...formState, [input.name]: value } as InitialState<T>))
  }

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    dispatch(action(formState))
  }

  const onButtonFormSubmit = (e: SyntheticEvent<HTMLButtonElement>) => {
    dispatch(action(formState))
  }

  const onReset = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.target as HTMLFormElement
    form.reset()
    formDispatch({ type: RESET_ACTION })
  }

  const resetForm = (chapter: string) => {
    const defaults = {
      [EDITOR_MAIN_SETTINGS.RENDER]: RENDER_FORM_STATE,
      [EDITOR_MAIN_SETTINGS.BEHAVIOUR]: BEHAVIOUR_FORM_STATE,
      [EDITOR_MAIN_SETTINGS.VIEW]: VIEW_FORM_STATE,
    }[chapter]
    if (defaults) {
      dispatch(action(defaults as unknown as InitialState<T>))
      formDispatch({ type: SET_DATA_ACTION, payload: defaults as unknown as FormInputData<T> })
      localStorage.removeItem(chapter)
    }
  }

  useEffect(() => {
    formDispatch({ type: RESET_ACTION })
  }, [ initialState ])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [ onClose ])

  return (
    <FormContext.Provider
      value={{
        onSubmit,
        onReset,
        handleInput,
        onButtonFormSubmit,
        resetForm,
      }}
    >
      {children}
    </FormContext.Provider>
  )
}
